import { useState, useRef, useCallback } from "react";

interface SwipeAction {
  label: string;
  icon?: React.ReactNode;
  color: string;
  onAction: () => void;
}

interface SwipeCardProps {
  children: React.ReactNode;
  leftAction?: SwipeAction;
  rightAction?: SwipeAction;
  threshold?: number;
  className?: string;
}

export default function SwipeCard({
  children,
  leftAction,
  rightAction,
  threshold = 80,
  className = "",
}: SwipeCardProps) {
  const [offsetX, setOffsetX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const isHorizontal = useRef<boolean | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isHorizontal.current = null;
    setIsSwiping(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isSwiping) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;

    // 判断滑动方向
    if (isHorizontal.current === null) {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        isHorizontal.current = Math.abs(dx) > Math.abs(dy);
      }
      return;
    }

    if (!isHorizontal.current) return;

    // 限制滑动范围
    const maxSwipe = threshold + 20;
    let newOffset = dx;
    if (!rightAction && newOffset > 0) newOffset = 0;
    if (!leftAction && newOffset < 0) newOffset = 0;
    newOffset = Math.max(-maxSwipe, Math.min(maxSwipe, newOffset));
    setOffsetX(newOffset);
  }, [isSwiping, threshold, leftAction, rightAction]);

  const handleTouchEnd = useCallback(() => {
    setIsSwiping(false);
    if (Math.abs(offsetX) >= threshold) {
      if (offsetX < 0 && leftAction) {
        leftAction.onAction();
      } else if (offsetX > 0 && rightAction) {
        rightAction.onAction();
      }
    }
    setOffsetX(0);
    isHorizontal.current = null;
  }, [offsetX, threshold, leftAction, rightAction]);

  const progress = Math.min(Math.abs(offsetX) / threshold, 1);
  const isTriggered = Math.abs(offsetX) >= threshold;

  return (
    <div className={`relative overflow-hidden rounded-lg ${className}`} ref={containerRef}>
      {/* 左滑背景（删除） */}
      {leftAction && offsetX < 0 && (
        <div
          className={`absolute inset-y-0 right-0 flex items-center justify-end px-4 transition-colors ${
            isTriggered ? leftAction.color : "bg-muted/50"
          }`}
          style={{ width: Math.abs(offsetX) }}
        >
          <div className={`flex items-center gap-1.5 text-xs font-medium transition-transform ${
            isTriggered ? "scale-110 text-white" : "text-muted-foreground"
          }`}>
            {leftAction.icon}
            <span>{leftAction.label}</span>
          </div>
        </div>
      )}
      {/* 右滑背景（启动） */}
      {rightAction && offsetX > 0 && (
        <div
          className={`absolute inset-y-0 left-0 flex items-center justify-start px-4 transition-colors ${
            isTriggered ? rightAction.color : "bg-muted/50"
          }`}
          style={{ width: Math.abs(offsetX) }}
        >
          <div className={`flex items-center gap-1.5 text-xs font-medium transition-transform ${
            isTriggered ? "scale-110 text-white" : "text-muted-foreground"
          }`}>
            {rightAction.icon}
            <span>{rightAction.label}</span>
          </div>
        </div>
      )}
      {/* 内容 */}
      <div
        className="relative z-10 bg-card"
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isSwiping ? "none" : "transform 0.3s ease-out",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}
